import {
  createContext,
  useContext,
  useCallback,
  useRef,
  useEffect,
  type ReactNode,
} from 'react';

interface AddNodeOptions {
  silent?: boolean;
}

type AddNodeHandler = (
  stepType: string,
  stepName?: string,
  options?: AddNodeOptions,
) => void;

interface WorkflowEditorContextValue {
  registerAddNodeHandler: (handler: AddNodeHandler | null) => void;
  addNode: (
    stepType: string,
    stepName?: string,
    options?: AddNodeOptions,
  ) => void;
}

const WorkflowEditorContext = createContext<WorkflowEditorContextValue | null>(
  null,
);

export function WorkflowEditorProvider({ children }: { children: ReactNode }) {
  const handlerRef = useRef<AddNodeHandler | null>(null);

  const registerAddNodeHandler = useCallback(
    (handler: AddNodeHandler | null) => {
      handlerRef.current = handler;
    },
    [],
  );

  const addNode = useCallback(
    (stepType: string, stepName?: string, options?: AddNodeOptions) => {
      handlerRef.current?.(stepType, stepName, options);
    },
    [],
  );

  return (
    <WorkflowEditorContext.Provider value={{ registerAddNodeHandler, addNode }}>
      {children}
    </WorkflowEditorContext.Provider>
  );
}

export function useWorkflowEditorContext() {
  const context = useContext(WorkflowEditorContext);
  if (!context) {
    throw new Error(
      'useWorkflowEditorContext must be used within a WorkflowEditorProvider',
    );
  }
  return context;
}

export function useRegisterAddNodeHandler(handler: AddNodeHandler | null) {
  const { registerAddNodeHandler } = useWorkflowEditorContext();

  useEffect(() => {
    registerAddNodeHandler(handler);
    return () => registerAddNodeHandler(null);
  }, [handler, registerAddNodeHandler]);
}
